/**
 * Helpers for OPD appointment booking (create + id resolution).
 */

export function resolveAppointmentId(appointment) {
  if (!appointment) return null;
  const raw =
    appointment.id ??
    appointment.appointmentId ??
    appointment.appointment_id ??
    appointment?.appointment?.id ??
    appointment?.appointment?.appointmentId ??
    appointment?.data?.id ??
    appointment?.data?.appointmentId ??
    null;
  if (raw == null || raw === "") return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : raw;
}

export function normalizeCreateAppointmentPayload(appointment = {}) {
  const payload = { ...appointment };
  if (payload.patientId != null && payload.patientId !== "") {
    payload.patientId = Number(payload.patientId);
  }
  if (payload.doctorId != null && payload.doctorId !== "") {
    payload.doctorId = Number(payload.doctorId);
  }
  return payload;
}

export function normalizeAppointmentResponse(data) {
  if (!data) return null;
  const id = resolveAppointmentId(data);
  if (id != null) {
    return {
      ...data,
      id,
      appointmentId: id,
    };
  }
  return data;
}

export function assertAppointmentCreated(data) {
  const normalized = normalizeAppointmentResponse(data);
  const id = resolveAppointmentId(normalized);
  if (!id) {
    throw new Error(
      "Appointment creation did not return a valid appointment ID. Please try again."
    );
  }
  return normalized;
}

/**
 * Build a PUT payload that updates only this appointment's datetime.
 * Other fields (status, token, prescription) are copied from that same record
 * so a partial PUT cannot wipe them or bleed onto other pending visits.
 */
export function buildReschedulePayloadForSingleAppointment(
  existingAppointment,
  appointmentDateTime,
  extras = {}
) {
  const id = resolveAppointmentId(existingAppointment);
  if (!id) {
    throw new Error("Cannot reschedule: appointment ID is missing.");
  }

  const payload = {
    patientId: Number(
      extras.patientId ??
        existingAppointment.patientId ??
        existingAppointment.patient_id
    ),
    doctorId: Number(
      extras.doctorId ??
        existingAppointment.doctorId ??
        existingAppointment.doctor_id
    ),
    appointmentDateTime,
    isCancel:
      existingAppointment.isCancel === true ||
      existingAppointment.is_cancel === true,
  };

  const status =
    existingAppointment.status || existingAppointment.appointmentStatus;
  if (status) {
    payload.status = status;
  }

  // AppointmentDTO only accepts camelCase. Snake_case aliases (token_status, etc.) return 400.
  const tokenStatus =
    existingAppointment.tokenStatus || existingAppointment.token_status;
  if (tokenStatus) {
    payload.tokenStatus = tokenStatus;
  }

  const tokenNumber =
    existingAppointment.tokenNumber ?? existingAppointment.token_number;
  if (
    tokenNumber != null &&
    tokenNumber !== "" &&
    String(tokenNumber) !== "0"
  ) {
    payload.tokenNumber = tokenNumber;
  }

  const appointmentType =
    existingAppointment.appointmentType || existingAppointment.appointment_type;
  if (appointmentType) {
    payload.appointmentType = appointmentType;
  }

  return { id, payload };
}
